import type { TFunction } from 'i18next';
import Transaction from '@/models/Transaction';
import type { PdfSection, PdfTableDocument } from '@/helpers/exportPdfHelper';
import { transactionTypeText } from '@/components/ui/DataTable/transactionTypeText';
import { isPlausibleCivilDate, parseTransactionDate, transactionCivilParts, transactionFullDateFormat } from '@/helpers/dateHelper';
import { isFutureRow } from './futureTransactions';
import siteLogo from '@/assets/images/logo1.svg';

/*
 * HAT-1399: «דף תנועות» - the transactions board as a PDF. It prints the board's own columns,
 * in the board's order and in the board's words, for the rows the board is showing: a reader
 * who compares the paper with the screen should find the same thing in both. The Excel export
 * stays the place for every field.
 */

const BIDI_MARKS = /[\u200e\u200f\u061c]/g;
const RECEIPT_KINDS = ['תרומה', 'פיקדון', 'הלוואה'];

interface TransactionsPdfInput {
  sections: PdfSection<Transaction>[];
  fund: { id: number; name?: string };
  /** The board's date range, as the API takes it (yyyy-mm-dd). */
  dateRange: { start?: string; end?: string };
  /** The fund's own currency, which the running balance is kept in. */
  fundCurrency?: string;
  /** A filter or the search hides some of the range's rows. */
  isFiltered: boolean;
  /** False for a coordinator who does not see all of the fund's money: a running balance over part of the rows is no balance. */
  withRunningBalance?: boolean;
  t: TFunction;
  language: string;
  now?: Date;
}

const formatDate = (value: unknown): string => {
  const date = parseTransactionDate(value);
  if (!date) return '—';
  if (!isPlausibleCivilDate(date)) {
    // As on the board (SPR-1179): shown, padded to four digits so year 15 does not read as 2015.
    const { year, month, day } = transactionCivilParts(date);
    return `${String(day).padStart(2, '0')}.${String(month).padStart(2, '0')}.${String(year).padStart(4, '0')}`;
  }
  return transactionFullDateFormat.format(date);
};

export function transactionsPdfDocument({
  sections,
  fund,
  dateRange,
  fundCurrency,
  isFiltered,
  withRunningBalance = true,
  t,
  language,
  now = new Date(),
}: TransactionsPdfInput): PdfTableDocument<Transaction> {
  const money = (value: number, currency: string | undefined, fractionDigits?: number) =>
    value
      .toLocaleString(language, {
        style: 'currency',
        currency: currency || 'ILS',
        ...(fractionDigits === undefined ? {} : { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits }),
      })
      .replace(BIDI_MARKS, '');

  const fundLabel = fund.name ? `${fund.id} | ${fund.name}` : String(fund.id);
  const start = dateRange.start ? formatDate(dateRange.start) : '';
  const end = dateRange.end ? formatDate(dateRange.end) : '';
  const generated = new Intl.DateTimeFormat(language === 'en' ? 'en-GB' : 'he-IL', { dateStyle: 'short', timeStyle: 'short' }).format(now);
  const title = t('transactionsStatementTitle');

  return {
    fileName: `${title} ${fundLabel} ${start}-${end}`
      .replace(/[\\/:*?"<>|]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim(),
    title,
    // The header's logo, which every site shows as it is.
    logoUrl: siteLogo,
    details: [
      fundLabel,
      ...(start && end ? [t('transactionsStatementPeriod', { start, end })] : []),
      t('pdfGeneratedAt', { date: generated }),
      ...(isFiltered ? [t('transactionsStatementFiltered')] : []),
    ],
    footerLabel: `${title} · ${fundLabel}`,
    pageLabel: (page, total) => t('pdfPageOf', { page, total }),
    emptyText: t('transactionsStatementEmpty'),
    // The same rule the app sets <html dir> by (i18n.ts).
    direction: language === 'he' ? 'rtl' : 'ltr',
    sections,
    columns: [
      { header: t('date'), width: 19, value: (row) => formatDate(row.date) },
      {
        header: t('amount'),
        width: 27,
        value: (row) => {
          const lines = [money(row.amount, row.currency, 2)];
          // The board shows the shekel figure of a foreign donation in a tooltip; on paper it is a second line.
          if (row.currency && row.currency !== 'ILS' && fundCurrency === 'ILS' && row.ilsAmount && row.exchangeRate) {
            lines.push(`(${money(row.ilsAmount, 'ILS', 2)})`);
          }
          if (row.future?.projected) lines.push(t('futurePendingExecution'));
          return lines.join('\n');
        },
      },
      ...(withRunningBalance
        ? [
            {
              header: t('cumulativeAmount'),
              width: 25,
              // HAT-1436: a row without a real running balance shows a dash, as on the board.
              value: (row: Transaction) => (isFutureRow(row) || !row.cumulativeAmount ? '—' : money(row.cumulativeAmount, fundCurrency || row.currency)),
            },
          ]
        : []),
      { header: t('transactionType'), width: 21, value: (row) => transactionTypeText(row.kind || '', t) },
      { header: t('transactionDetails'), value: (row) => row.contactName || '' },
      { header: t('paymentMethod'), width: 23, value: (row) => (row.paymentMethod ? t(row.paymentMethod) : '') },
      {
        header: t('receipt'),
        width: 22,
        value: (row) => {
          if (!RECEIPT_KINDS.includes(row.kind)) return '';
          if (!row.receiptId) return row.future ? '—' : t('noReceipt');
          return String(row.receiptId);
        },
      },
    ],
  };
}
